import { searchPromotedEvents } from '@eventbrite/ads';
import { fetchJSON } from '@eventbrite/http';
import { getLocationData } from '@eventbrite/redux-destination';
import { getExperiment } from '@eventbrite/statsig';
import isEmpty from 'lodash/isEmpty';
import { formatUrl } from 'url-lib';
import { PriceType } from '../../../../constants/prices';
import { ALL_TAB, FOR_YOU, TABS_CONFIG } from '../../../../constants/tabConfig';
import { fetchThingsToDoShelfData } from '../../../../redux/actions/thingsToDoShelf';
import {
    BucketShape,
    EventCategoryCarouselExperiment,
} from '../../../../types/index';
import { buildBBox } from '../../utils';
import {
    setFeCallAsFailed,
    setFeCallAsOngoing,
    setFeCallAsSuccess,
} from '../app/actions';
import { HOME_TABS_SLOTS } from '../constants';
import { fetchEventsForTab, fetchFeaturedPaidEvents } from '../events/actions';
import { getInitialState } from '../index';
import { UPDATE_ROOT_CONTEXT } from './rootActionTypes';

function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getRestOfContext =
    (originalContext: any, tabKey: string) => (dispatch: Function) => {
        const isUserLogged = !!originalContext.user.isAuthenticated;
        const isInNewHomepageExperiment =
            !isUserLogged &&
            getExperiment(
                EventCategoryCarouselExperiment.name,
                EventCategoryCarouselExperiment.paramName,
                EventCategoryCarouselExperiment.values.control,
            ) === EventCategoryCarouselExperiment.values.base;

        const excludeEvents = tabKey === FOR_YOU;
        const isAllTab = tabKey === ALL_TAB;
        dispatch(setFeCallAsOngoing());

        const tabConfig = TABS_CONFIG[tabKey];

        fetchJSON(
            formatUrl('/fe/', {
                // Optionally exclude events from unnecessarily getting fetched
                // For example, when the user lands on the For You tab either as the
                // default tab or through refresh, events should not be fetched.
                excludeEvents,
            }),
        )
            .then(async (response: any) => {
                let feResponse = response;

                if (window?.__SERVER_DATA__?.featureFlags) {
                    window.__SERVER_DATA__.featureFlags.getPromotedEventsFromGenericEndpoint =
                        response.featureFlags.getPromotedEventsFromGenericEndpoint;
                }

                if (response.flatBucket.__should_suggest_reloading_page) {
                    // The homepage backend has a performance optimization where it will always attempt to retrieve
                    // events from memcache. If the cache is cold, then it will return __should_suggest_reloading_page to
                    // the frontend. If that happens, we want to wait 1000ms and retry one time, hoping that it's
                    // enough time to populate the backend cache.
                    await sleep(1000);
                    feResponse = await fetchJSON(
                        formatUrl('/fe/', {
                            excludeEvents,
                        }),
                    );
                }
                const filteredResponse = feResponse;

                if (!isEmpty(filteredResponse.buckets)) {
                    //we filter out the bucket "liked_events" from the response so we only use the liked_events from the api call
                    filteredResponse.buckets = response.buckets.filter(
                        (bucket: BucketShape) => bucket.key !== 'liked_events',
                    );
                }

                const shouldFetchPromotedEvents =
                    isAllTab && !isInNewHomepageExperiment;

                const promotedEvents = shouldFetchPromotedEvents
                    ? await searchTabPromotedEvents(tabConfig.eventSearch)
                    : [];

                response.flatBucket.promoted_results = promotedEvents;

                const fullContext = getInitialState({
                    ...originalContext,
                    ...filteredResponse,
                });

                if (fullContext.content.browseState.tabKey !== ALL_TAB) {
                    fullContext.content.flatBucket = {};
                }

                const {
                    content: {
                        flatBucket,
                        browseState: { tabKey },
                    },
                    location: { placeId, latitude, longitude },
                } = fullContext;

                dispatch(
                    fetchFeaturedPaidEvents({
                        placeId: placeId || '',
                        isFlagEnable:
                            fullContext.app.featureFlags
                                ?.enableFeaturedEvents || false,
                    }),
                );
                dispatch({
                    type: UPDATE_ROOT_CONTEXT,
                    payload: fullContext,
                });

                // TODO: fix the server to perform a search when the cookie contains a lat/lng.
                // Currently, the server only looks for a place id when deciding whether to supply
                // events in the context for the client.  We work around the problem by issuing
                // an ajax search request.  Once the server is fixed, remove the code below.
                if (isEmpty(flatBucket) && !placeId && latitude && longitude) {
                    dispatch(
                        fetchEventsForTab({
                            tabKey,
                            locationData: {
                                bbox: buildBBox({ latitude, longitude }),
                            },
                        }),
                    );
                }
                dispatch(fetchThingsToDoShelfData());
                dispatch(setFeCallAsSuccess());
            })
            .catch(() => {
                dispatch(setFeCallAsFailed());
            });
    };

function searchTabPromotedEvents(tabEventSearch: {
    price?: PriceType;
    tags?: Array<string>;
    online_events_only: boolean;
}) {
    const locationCookie = tabEventSearch.online_events_only
        ? ONLINE_LOCATION
        : getStoredLocation();

    return searchPromotedEvents({
        slots: HOME_TABS_SLOTS.length,
        page: 1,
        interface: 'homepage',
        search: {
            dates: ['current_future'],
            price: tabEventSearch?.price || 'paid',
            tags: tabEventSearch?.tags || [],
            languages: [],
            place: {
                is_online: locationCookie.is_online,
                geo_point: locationCookie.is_online
                    ? undefined
                    : {
                          lon: locationCookie.longitude,
                          lat: locationCookie.latitude,
                          radius: 50,
                      },
                place_id: locationCookie.place_id?.toString(),
            },
        },
    });
}

const ONLINE_LOCATION = {
    is_online: true,
    longitude: 0,
    latitude: 0,
    place_id: undefined,
};

function getStoredLocation(): {
    is_online: boolean;
    latitude: number;
    longitude: number;
    place_id?: string;
} {
    const location = getLocationData();
    if (!location) {
        return ONLINE_LOCATION;
    }
    return {
        is_online: false,
        longitude: location.longitude || 0,
        latitude: location.latitude || 0,
        place_id: location.place_id ? location.place_id : undefined,
    };
}
